/** Four deliberately uneven feed sacks stacked on a standard timber pallet. */
import * as THREE from 'three';
import { surface } from '../materials';
import { seatOnOrigin } from '../origin';
import type { AssetDefinition } from '../types';

interface SackShape {
  width: number;
  height: number;
  length: number;
  edgeSlump: number;
  centreSag: number;
}

function sackGeometry(shape: SackShape): THREE.BufferGeometry {
  const coordinates = [-1, -0.45, 0, 0.45, 1];
  const positions: number[] = [];
  const topY = shape.height / 2;
  const halfWidth = shape.width / 2;
  const halfLength = shape.length / 2;
  const sideWallTop = topY - shape.edgeSlump;
  // The visible top is allowed to settle below an ideal rectangular fill level,
  // but never below the seam where the side wall meets it. Letting that centre
  // point pass the seam made the sack read as an open trough in the asset gate.
  const filledTopFloor = sideWallTop + 0.004;

  for (let zIndex = 0; zIndex < coordinates.length; zIndex += 1) {
    const z = coordinates[zIndex]!;
    for (let xIndex = 0; xIndex < coordinates.length; xIndex += 1) {
      const x = coordinates[xIndex]!;
      const corner = Math.abs(x) === 1 && Math.abs(z) === 1;
      const edge = Math.abs(x) === 1 || Math.abs(z) === 1;
      // Insets only the four corners: without it this is a softened box, while
      // a filled sack needs its perimeter to lose the manufactured right angle.
      const cornerInset = corner ? 0.16 : 0;
      const px = halfWidth * x * (1 - cornerInset);
      const pz = halfLength * z * (1 - cornerInset);
      const y = corner || edge
        ? sideWallTop
        : x === 0 && z === 0
          ? Math.max(filledTopFloor, topY - shape.centreSag)
          : topY;
      positions.push(px, y, pz);
    }
  }

  const indices: number[] = [];
  for (let z = 0; z < 4; z += 1) {
    for (let x = 0; x < 4; x += 1) {
      const a = z * 5 + x;
      const b = a + 1;
      const c = a + 5;
      const d = c + 1;
      indices.push(a, c, b, b, c, d);
    }
  }

  const perimeter = [0, 1, 2, 3, 4, 9, 14, 19, 24, 23, 22, 21, 20, 15, 10, 5];
  const bottomStart = positions.length / 3;
  for (const topIndex of perimeter) {
    const index = topIndex * 3;
    const x = positions[index];
    const z = positions[index + 2];
    if (x === undefined || z === undefined) throw new Error('feed-sacks: malformed sack perimeter');
    positions.push(x, -topY, z);
  }
  for (let index = 0; index < perimeter.length; index += 1) {
    const next = (index + 1) % perimeter.length;
    const a = perimeter[index]!;
    const b = perimeter[next]!;
    const c = bottomStart + index;
    const d = bottomStart + next;
    indices.push(a, c, b, b, c, d);
  }
  const bottomCentre = positions.length / 3;
  positions.push(0, -topY, 0);
  for (let index = 0; index < perimeter.length; index += 1) {
    const next = (index + 1) % perimeter.length;
    indices.push(bottomCentre, bottomStart + next, bottomStart + index);
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

function mesh(geometry: THREE.BufferGeometry, material: THREE.Material, name: string): THREE.Mesh {
  const part = new THREE.Mesh(geometry, material);
  part.name = name;
  part.castShadow = true;
  part.receiveShadow = true;
  return part;
}

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'feed-sacks';

  const wood = surface('organic', 0x625c49);
  const sackMaterials = [
    surface('fabric', 0x87816f),
    surface('fabric', 0x7b7868),
    surface('fabric', 0x918a76),
    surface('fabric', 0x706d5e),
  ] as const;

  const pallet = new THREE.Group();
  pallet.name = 'timber-pallet';
  for (const [x, name] of [[-0.39, 'pallet-bearer-left'], [0, 'pallet-bearer-centre'], [0.39, 'pallet-bearer-right']] as const) {
    const bearer = mesh(new THREE.BoxGeometry(0.12, 0.08, 0.80), wood, name);
    bearer.position.set(x, 0.04, 0);
    pallet.add(bearer);
  }
  for (const [z, name] of [[-0.34, 'deck-board-back'], [-0.17, 'deck-board-rear'], [0, 'deck-board-centre'], [0.17, 'deck-board-front'], [0.34, 'deck-board-forward']] as const) {
    const board = mesh(new THREE.BoxGeometry(1.05, 0.06, 0.12), wood, name);
    board.position.set(0, 0.10, z);
    pallet.add(board);
  }
  group.add(pallet);

  const sacks = new THREE.Group();
  sacks.name = 'uneven-four-sack-stack';
  const specifications: readonly [SackShape, readonly [number, number, number], number, string, THREE.Material][] = [
    [{ width: 0.38, height: 0.135, length: 0.72, edgeSlump: 0.014, centreSag: 0.011 }, [-0.37, 0.1975, -0.015], -0.045, 'lower-sack-left', sackMaterials[0]],
    [{ width: 0.39, height: 0.148, length: 0.75, edgeSlump: 0.018, centreSag: 0.014 }, [0, 0.204, 0.01], 0.025, 'lower-sack-centre', sackMaterials[1]],
    [{ width: 0.37, height: 0.13, length: 0.70, edgeSlump: 0.012, centreSag: 0.009 }, [0.37, 0.195, -0.025], -0.03, 'lower-sack-right', sackMaterials[2]],
    [{ width: 0.62, height: 0.125, length: 0.34, edgeSlump: 0.014, centreSag: 0.012 }, [-0.08, 0.3275, 0.085], 0.52, 'cross-stacked-top-sack', sackMaterials[3]],
  ];
  for (const [shape, position, yaw, name, material] of specifications) {
    const sack = mesh(sackGeometry(shape), material, name);
    sack.position.set(...position);
    sack.rotation.y = yaw;
    sacks.add(sack);
  }
  group.add(sacks);

  seatOnOrigin(group);
  return group;
}

export const feedSacksAsset: AssetDefinition = {
  id: 'feed-sacks',
  label: 'Feed sacks on pallet',
  description: 'Four slumped drab feed sacks, unevenly stacked on a timber pallet.',
  footprint: { width: 1.14, height: 0.39, length: 0.80 },
  create: build,
};
